import React from 'react';
import { Link } from 'react-router-dom';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from '../Education/ScienceOfTime.module.scss';
import { APP_YEAR } from '../../../utils/constants';
import { tokens } from '../../../styles/tokens';

const linkStyle = { color: tokens.skyBlue, textDecoration: 'none', fontWeight: 600 } as const;

export const BlogPostVisualTools: React.FC = () => {
  return (
    <StaticPageLayout>
      <div className={styles.container}>
        <div style={{ marginBottom: '24px' }}>
          <Link to="/blog" style={linkStyle}>
            ← Back to Blog
          </Link>
        </div>

        <div className={styles.header}>
          <h1>The Tools That Actually Help</h1>
          <p className={styles.subtitle}>
            Visual time tools work when they are used correctly. Used poorly, they can increase stress instead of reducing it. Let's unpack why.
          </p>
        </div>

        <section className={styles.section}>
          <p>
            By now, two things should be clear:
          </p>
          <ul className={styles.tipsList}>
            <li>Young children <Link to="/blog/abstract-time" style={linkStyle}>don’t experience time</Link> the way adults do</li>
            <li><Link to="/blog/routine-engine" style={linkStyle}>Predictable routines</Link>—not reminders—are what make transitions faster</li>
          </ul>
          <p>
            This final piece answers the natural next question parents ask: <strong>So what tools actually help—and which ones quietly make things worse?</strong>
          </p>
        </section>

        <section className={styles.section}>
          <h2>Why Kids Need to See Time</h2>
          <p>
            For young children, time isn’t something you calculate. <strong>It’s something you observe.</strong>
          </p>
          <p>
            Developmental research consistently shows that children under ~7–8 years old struggle with estimating duration, anticipating an endpoint, and emotionally preparing for a change.
          </p>

          <div className={styles.quoteBox}>
            <p>“Children aged 2–4 years old showed a significant increase in self-regulation skills…”</p>
            <span className={styles.source}>— CEC (Summary of FAU study)</span>
          </div>
          
          <div className={styles.statCard} style={{ margin: '32px 0' }}>
            <p style={{ fontStyle: 'italic', color: tokens.charcoal }}>
              This is why alarms and verbal countdowns often fail. An alarm doesn’t show time passing. It just announces that time is over.
            </p>
          </div>
          
          <p>From the child’s point of view, that feels abrupt—and often unfair.</p>
        </section>

        <section className={styles.section}>
          <h2>What Visual Timers Do Differently</h2>
          <p>
            Visual timers (especially wedge-based ones) represent time as space. Instead of numbers counting down, children see a shape shrinking, a color disappearing, and progress unfolding in real time.
          </p>

          <div className={styles.quoteBox}>
            <p>“This study investigated the impact of visual timers on 7- to 9-year-old students’… anticipatory anxiety… and on-task behavior…”</p>
            <span className={styles.source}>— MDPI</span>
          </div>

          <p>
            This matters because it supports something called <strong>anticipatory regulation</strong>: the ability to prepare emotionally and behaviorally for what comes next.
          </p>
          <p>
            Research in early childhood education has shown that when time is represented visually:
          </p>
          <ul className={styles.tipsList}>
            <li>Children transition more smoothly</li>
            <li>Engagement increases</li>
            <li>Emotional regulation improves during task changes</li>
          </ul>
          <p>
            In simple terms: children stop being surprised by the ending.
          </p>
        </section>

        <section className={styles.section}>
          <h2>Why Wedges Beat Digital Countdowns</h2>
          <p>
            Digital timers show numbers. Children don’t yet know what those numbers feel like. Wedges show progress.
          </p>
          <p>
            A wedge-based clock answers the question children are actually asking: <strong>"How close are we?"</strong> Not: "What number is it now?"
          </p>
          <p>
            Children don’t need precision. They need visibility.
          </p>
        </section>

        <section className={styles.section}>
          <h2>The Parent Protocol (This Part Matters)</h2>
          <p>
            Visual time works only when paired with the right setup. Here’s what research and practitioner evidence agree on.
          </p>
          
          <div style={{ background: 'white', padding: '32px', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', marginTop: '24px' }}>
            <h3 style={{ marginTop: 0, fontSize: '1.4rem' }}>1. The Parent Must Be Ready First</h3>
            <p>If the timer ends and you’re still looking for keys or packing bags, the system collapses. Visual time works when the transition can actually happen immediately.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>2. The Timer Is Information, Not a Threat</h3>
            <p>Avoid loud alarms or "When this hits zero, we're done". Instead, let the wedge quietly disappear and narrate calmly: "The time is almost finished." This reduces anxiety.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>3. Pair Time With a Role</h3>
            <p>Children transition faster when they have a job. "When the wedge is gone, you unlock the door." This shifts the child from losing control to having purpose.</p>
            
            <h3 style={{ fontSize: '1.4rem' }}>4. Always Follow Through</h3>
            <p>Trust is everything. If the wedge ends and nothing happens, the child learns the timer is meaningless. Consistency builds credibility.</p>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Visual Time + Routine = The Real Win</h2>
          <p>
            Timers alone help. Routines alone help. Together, they change behavior.
          </p>

          <div className={styles.quoteBox}>
            <p>“Using a visual schedule at home provides young children with predictability… and consistency…”</p>
            <span className={styles.source}>— Pyramid Model Innovations</span>
          </div>

          <ul className={styles.tipsList}>
            <li>The routine answers: <strong>"What happens next?"</strong></li>
            <li>Visual time answers: <strong>"When does it change?"</strong></li>
          </ul>
          <p>
            This combination reduces negotiation, reduces emotional spikes, and builds genuine time awareness over weeks, not days. Over time, children internalize the rhythm. You’ll notice less reminding, faster readiness, and more independence.
          </p>
          <p>
            That’s not control. That’s learning.
          </p>
        </section>

        <section className={styles.section}>
          <h2>The Big Takeaway From This Series</h2>
          <ul className={styles.tipsList}>
            <li>Children don’t understand abstract time early on</li>
            <li>Routines create speed by removing decisions</li>
            <li>Visual wedges make time feel real and predictable</li>
          </ul>
          <p>
            When these work together, transitions stop being a fight—and start becoming a habit. That’s how you leave the house on time. Not once. But consistently.
          </p>
        </section>

        <div className={styles.cta}>
          <h3>Start your visual routine today.</h3>
          <Link to="/register" className={styles.primaryButton}>
            Get MyMinu Free
          </Link>
        </div>

        <div className={styles.sourcesSection}>
          <h3>Sources</h3>
          
          <div className={styles.sourceItem}>
            <p>MDPI ({APP_YEAR}). Time on Their Side: How Visual Timers Affect Anticipatory Anxiety, Performance, and On-Task Behavior…</p>
            <a href="https://www.mdpi.com/2254-9625/15/12/243" target="_blank" rel="noopener noreferrer">
              https://www.mdpi.com/2254-9625/15/12/243
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>Time Timer / Exceptional Children org PDF (references an FAU study summary)</p>
            <a href="https://exceptionalchildren.org/sites/default/files/2023-09/CR22803_Guide_IEP_Rev1_081123_web.pdf" target="_blank" rel="noopener noreferrer">
              https://exceptionalchildren.org/sites/default/files/2023-09/CR22803_Guide_IEP_Rev1_081123_web.pdf
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>National Center for Pyramid Model Innovations (2020). Visual Supports for Routines, Schedules, and Transitions.</p>
            <a href="https://www.challengingbehavior.org/docs/Routine_cards_home.pdf" target="_blank" rel="noopener noreferrer">
              https://www.challengingbehavior.org/docs/Routine_cards_home.pdf
            </a>
          </div>

          <div className={styles.sourceItem}>
            <p>NAEYC (Young Children, 2018): Reducing Challenging Behaviors during Transitions…</p>
            <a href="https://www.naeyc.org/resources/pubs/yc/sep2018/reducing-challenging-behaviors-during-transitions" target="_blank" rel="noopener noreferrer">
              https://www.naeyc.org/resources/pubs/yc/sep2018/reducing-challenging-behaviors-during-transitions
            </a>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};
